import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const ORDER_OPTIONS = [
  "Under 1,000 orders / month",
  "1,000 – 5,000 orders / month",
  "5,000 – 20,000 orders / month",
  "20,000+ orders / month",
];

const inputCls =
  "w-full rounded-xl border border-edge bg-ink/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-mint/60 focus:ring-2 focus:ring-mint/20 sm:text-base";

export const Waitlist = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    website: "",
    monthly_orders: "",
    company: "",
  });
  const [status, setStatus] = useState(
    localStorage.getItem("tv_joined") ? "success" : "idle"
  );
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.website.trim() || !form.monthly_orders) {
      setError("Please fill in every field.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/waitlist`, form);
      localStorage.setItem("tv_joined", "1");
      setStatus("success");
    } catch (err) {
      if (err.response?.status === 409) {
        localStorage.setItem("tv_joined", "1");
        setStatus("duplicate");
      } else if (err.response?.status === 422) {
        setError("That email doesn't look right. Check it and try again.");
        setStatus("idle");
      } else {
        setError("Something went wrong. Try again in a moment.");
        setStatus("idle");
      }
    }
  };

  return (
    <section
      id="waitlist"
      className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8"
      data-testid="waitlist-section"
    >
      <div
        className="absolute left-1/2 top-1/2 h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-mint/10 blur-[130px]"
        aria-hidden="true"
      />
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative mx-auto max-w-xl"
      >
        <p className="text-center font-mono text-xs uppercase tracking-[0.24em] text-mint">
          03 — Early access
        </p>
        <h2 className="mt-4 text-center font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
          Join the waitlist
        </h2>
        <p className="mt-4 text-center text-sm text-slate-400 sm:text-base">
          We're onboarding a small group of Shopify brands first.
        </p>

        <div className="mt-10 rounded-2xl border border-edge bg-panel/80 p-6 backdrop-blur sm:p-8">
          <AnimatePresence mode="wait">
            {status === "success" || status === "duplicate" ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex flex-col items-center py-8 text-center"
                data-testid="waitlist-success-message"
              >
                <CheckCircle2 className="mb-4 h-12 w-12 text-mint" strokeWidth={1.5} />
                <p className="font-display text-xl font-bold text-slate-50">
                  You're on the list — talk soon.
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  We'll reach out from hello@truvon.tech.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.3 }}
                onSubmit={submit}
                className="space-y-4"
                data-testid="waitlist-form"
                noValidate
              >
                <input
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                  data-testid="waitlist-name-input"
                  autoComplete="name"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Work email"
                  className={inputCls}
                  data-testid="waitlist-email-input"
                  autoComplete="email"
                />
                <input
                  type="text"
                  value={form.website}
                  onChange={set("website")}
                  placeholder="Store website (e.g. yourbrand.com)"
                  className={inputCls}
                  data-testid="waitlist-website-input"
                  autoComplete="url"
                />
                <select
                  value={form.monthly_orders}
                  onChange={set("monthly_orders")}
                  className={`${inputCls} appearance-none ${form.monthly_orders ? "" : "text-slate-600"}`}
                  data-testid="waitlist-orders-select"
                >
                  <option value="" disabled>
                    Monthly order volume
                  </option>
                  {ORDER_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-panel text-slate-100">
                      {o}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={form.company}
                  onChange={set("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />
                {error && (
                  <p className="text-sm text-rose-400" data-testid="waitlist-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="waitlist-submit-button"
                  className="group flex w-full items-center justify-center gap-2 rounded-xl bg-mint px-6 py-3.5 font-display text-base font-bold text-ink transition-colors duration-200 hover:bg-mint-hover disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Join the waitlist
                      <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
};
